/**
 * Utility functions for determining MTB race categories based on age
 */

// Category definitions based on the age a rider will turn in the current year
export interface CategoryDefinition {
  code: string;
  category: string;
  minAge: number;
  maxAge: number;
  gender: 'male' | 'female';
}

// All MTB categories as defined by UCI rules
export const MTB_CATEGORIES: CategoryDefinition[] = [
  // National entry level
  { code: 'NG', category: 'Nipper Girls', minAge: 8, maxAge: 10, gender: 'female' },
  { code: 'NB', category: 'Nipper Boys', minAge: 8, maxAge: 10, gender: 'male' },
  { code: 'SG', category: 'Sprog Girls', minAge: 11, maxAge: 12, gender: 'female' },
  { code: 'SB', category: 'Sprog Boys', minAge: 11, maxAge: 12, gender: 'male' },
  { code: 'JG', category: 'Sub Junior Girls', minAge: 13, maxAge: 14, gender: 'female' },
  { code: 'JB', category: 'Sub Junior Boys', minAge: 13, maxAge: 14, gender: 'male' },
  { code: 'YW', category: 'Youth Women', minAge: 15, maxAge: 16, gender: 'female' },
  { code: 'YM', category: 'Youth Men', minAge: 15, maxAge: 16, gender: 'male' },
  
  // UCI Categories
  { code: 'JW', category: 'Junior Women', minAge: 17, maxAge: 18, gender: 'female' },
  { code: 'JM', category: 'Junior Men', minAge: 17, maxAge: 18, gender: 'male' },
  { code: 'UW', category: 'Under 23 Women', minAge: 19, maxAge: 22, gender: 'female' },
  { code: 'UM', category: 'Under 23 Men', minAge: 19, maxAge: 22, gender: 'male' },
  { code: 'SW', category: 'Senior Women', minAge: 23, maxAge: 29, gender: 'female' },
  { code: 'SM', category: 'Senior Men', minAge: 23, maxAge: 29, gender: 'male' },
  { code: 'EW', category: 'Elite Women', minAge: 23, maxAge: 200, gender: 'female' }, // No upper limit, using 200 as a placeholder
  { code: 'EM', category: 'Elite Men', minAge: 23, maxAge: 200, gender: 'male' }, // No upper limit, using 200 as a placeholder
  
  // UCI Masters Categories
  { code: 'SX', category: 'Sub Vet Women', minAge: 30, maxAge: 39, gender: 'female' },
  { code: 'SV', category: 'Sub Vet Men', minAge: 30, maxAge: 39, gender: 'male' },
  { code: 'VW', category: 'Veteran Women', minAge: 40, maxAge: 49, gender: 'female' },
  { code: 'VM', category: 'Veteran Men', minAge: 40, maxAge: 49, gender: 'male' },
  { code: 'MW', category: 'Masters Women', minAge: 50, maxAge: 59, gender: 'female' },
  { code: 'MM', category: 'Masters Men', minAge: 50, maxAge: 59, gender: 'male' },
  { code: 'GW', category: 'Grand Masters Women', minAge: 60, maxAge: 200, gender: 'female' }, // No upper limit, using 200 as a placeholder
  { code: 'GM', category: 'Grand Masters Men', minAge: 60, maxAge: 200, gender: 'male' }, // No upper limit, using 200 as a placeholder
];

/**
 * Calculate the age a person will turn in the current year based on their date of birth
 * @param dateOfBirth Date of birth as a Date object or ISO string
 * @returns The age the person will turn in the current year
 */
export function calculateAgeInCurrentYear(dateOfBirth: Date | string): number {
  const dob = typeof dateOfBirth === 'string' ? new Date(dateOfBirth) : dateOfBirth;
  const currentYear = new Date().getFullYear();
  return currentYear - dob.getFullYear();
}

/**
 * Determine the MTB category for a rider based on their date of birth and gender
 * @param dateOfBirth Date of birth as a Date object or ISO string
 * @param gender 'male' or 'female'
 * @returns The category definition or null if no matching category is found
 */
export function determineMTBCategory(
  dateOfBirth: Date | string, 
  gender: 'male' | 'female'
): CategoryDefinition | null {
  const ageThisYear = calculateAgeInCurrentYear(dateOfBirth);
  
  // Find the appropriate category based on the age the rider will turn this year and their gender
  const category = MTB_CATEGORIES.find(cat => 
    cat.gender === gender && 
    ageThisYear >= cat.minAge && 
    ageThisYear <= cat.maxAge
  );
  
  return category || null;
}

/**
 * Special handling for Elite categories which may be chosen by riders who qualify
 * for Senior categories but compete at Elite level
 * @param dateOfBirth Date of birth as a Date object or ISO string
 * @param gender 'male' or 'female'
 * @returns The Elite category if the rider is eligible, null otherwise
 */
export function getEliteCategoryIfEligible(
  dateOfBirth: Date | string, 
  gender: 'male' | 'female'
): CategoryDefinition | null {
  const ageThisYear = calculateAgeInCurrentYear(dateOfBirth);
  
  // Elite categories are available for riders 23 and older
  if (ageThisYear >= 23) {
    return gender === 'male' 
      ? MTB_CATEGORIES.find(cat => cat.code === 'EM') || null
      : MTB_CATEGORIES.find(cat => cat.code === 'EW') || null;
  }
  
  return null;
}

/**
 * Get all categories a rider is eligible for (usually just one, but could include Elite option)
 * @param dateOfBirth Date of birth as a Date object or ISO string
 * @param gender 'male' or 'female'
 * @returns Array of categories the rider is eligible for
 */
export function getAllEligibleCategories(
  dateOfBirth: Date | string, 
  gender: 'male' | 'female'
): CategoryDefinition[] {
  const primaryCategory = determineMTBCategory(dateOfBirth, gender);
  const eliteCategory = getEliteCategoryIfEligible(dateOfBirth, gender);
  
  const categories: CategoryDefinition[] = [];
  if (primaryCategory) categories.push(primaryCategory);
  if (eliteCategory && eliteCategory.code !== primaryCategory?.code) {
    categories.push(eliteCategory);
  }
  
  return categories;
}
